/**
 * Groups catalogue-picker search hits into ONE entry per articleNumber, with a chip per active
 * season the product exists in.
 *
 * Why chips and not a "collapse duplicates" rule: two live records sharing an articleNumber can
 * differ in articleName/color/color_fr (the legacy uploader's carryover path freezes those fields
 * at the season that created the doc — uploadNewSeasonArticles.js:149-167), and the fw26_ca size
 * remediation left pairs that are identical in price AND sizes but differ in those labels. Hiding
 * either record would also make the seasons it carries unplaceable. So nothing is ever hidden:
 * every record carrying an active season is reachable through its chip.
 *
 * @typedef {{seasonKey: string, name: string, name_fr: string, record: object}} SeasonChip
 * @typedef {{articleNumber: string, chips: SeasonChip[], defaultChip: SeasonChip,
 *            priceVaries: boolean, sizesVaries: boolean}} ProductGroup
 */

// Season docs carry only long display names ("Spring/Summer 26") — there is no shortName field in
// `general/seasonInfo`. Three long chips wrap onto a second line, which makes cards in the same grid
// row different heights, so chips render the trade-standard abbreviation instead. Derived from the
// season KEY, never parsed out of the name: the key is stable and language-independent.
const SEASON_ABBREVIATIONS = {
  ss: { en: "SS", fr: "PE" },
  fw: { en: "FW", fr: "AH" }
};

const SEASON_KEY_SHAPE = /^(ss|fw)(\d{2})(_|$)/;

/**
 * @param {{seasonKey: string, name: string, name_fr: string}} chip
 * @param {string} language i18next language ("en" or otherwise)
 * @returns {string} e.g. "SS26" / "PE26", or the full season name if the key is an unknown shape
 */
export const shortSeasonLabel = (chip, language) => {
  if (chip == null) return "";
  const isEnglish = language === "en";
  const fullName = (isEnglish ? chip.name : chip.name_fr) || chip.name || "";

  const match = SEASON_KEY_SHAPE.exec(chip.seasonKey || "");
  if (match == null) return fullName;

  return `${SEASON_ABBREVIATIONS[match[1]][isEnglish ? "en" : "fr"]}${match[2]}`;
};

const sameSizeSet = (a, b) => {
  const x = a || [];
  const y = b || [];
  if (x.length !== y.length) return false;
  const set = new Set(x);
  return y.every((size) => set.has(size));
};

/**
 * @param {object[]} records raw Typesense article documents
 * @param {{key: string, name: string, name_fr: string}[]} activeSeasons chronological ASCENDING
 * @returns {ProductGroup[]} first-appearance order (preserves Typesense relevance ranking)
 */
export const buildProductGroups = (records, activeSeasons) => {
  const seasonByKey = new Map((activeSeasons || []).map((season) => [season.key, season]));
  const seasonOrder = (activeSeasons || []).map((season) => season.key);

  // Map preserves insertion order, so first-appearance ranking survives.
  const recordsByArticleNumber = new Map();
  (records || []).forEach((record) => {
    if (record == null || record.dropped || record.articleNumber == null) return;
    const existing = recordsByArticleNumber.get(record.articleNumber);
    if (existing) existing.push(record);
    else recordsByArticleNumber.set(record.articleNumber, [record]);
  });

  const groups = [];
  recordsByArticleNumber.forEach((groupRecords, articleNumber) => {
    // seasonKey -> the record carrying it. A season should belong to exactly one record; when two
    // claim it (the corruption classify.ts guards with `ambiguous-current`), take the lowest id so
    // the render is stable rather than dependent on hit order.
    const recordBySeason = new Map();
    groupRecords.forEach((record) => {
      (record.seasons || []).forEach((seasonKey) => {
        if (!seasonByKey.has(seasonKey)) return;
        const existing = recordBySeason.get(seasonKey);
        if (existing == null || String(record.id) < String(existing.id)) {
          recordBySeason.set(seasonKey, record);
        }
      });
    });

    const chips = [...recordBySeason.entries()]
      .sort(([a], [b]) => seasonOrder.indexOf(b) - seasonOrder.indexOf(a))
      .map(([seasonKey, record]) => ({
        seasonKey,
        name: seasonByKey.get(seasonKey).name,
        name_fr: seasonByKey.get(seasonKey).name_fr,
        record
      }));

    // No active season means no chip, which means no way to add it and no season to stamp.
    // The search filter should make this impossible; drop it rather than render a dead card.
    if (chips.length === 0) return;

    const chipRecords = chips.map((chip) => chip.record);
    const first = chipRecords[0];

    groups.push({
      articleNumber,
      chips,
      defaultChip: chips[0],
      priceVaries: !chipRecords.every((r) => r.retailPrice === first.retailPrice),
      sizesVaries: !chipRecords.every((r) =>
        sameSizeSet(r.userFriendlySizes, first.userFriendlySizes)
      )
    });
  });

  return groups;
};
